import { useState } from "react";
import { AlertCircle, CheckCircle, Copy, Loader2, XCircle } from "lucide-react";
import { ContractNameValidationResult, validateContractNamesPriceUpdateValidateContractNamesGet } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";

export function ContractNameValidationDialog() {
  const [isLoading, setIsLoading] = useState(false);
  const [validationResults, setValidationResults] = useState<ContractNameValidationResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const handleValidateContractNames = async () => {
    setIsLoading(true);
    setError(null);
    setValidationResults(null);

    try {
      const result = await validateContractNamesPriceUpdateValidateContractNamesGet();
      setValidationResults(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (data: ContractNameValidationResult) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast({
      title: "Copied to clipboard",
      description: "The raw data has been copied to your clipboard",
    });
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button onClick={handleValidateContractNames} disabled={isLoading} variant="secondary">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Validating...
            </>
          ) : (
            <>
              <AlertCircle className="mr-2 h-4 w-4" />
              Validate Contract Names
            </>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="flex h-[95vh] max-w-screen-lg flex-col justify-start gap-2 overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Contract Name Validation Results</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="mb-3 h-10 w-10 animate-spin text-primary" />
            <p className="text-lg text-muted-foreground">Validating contract names...</p>
          </div>
        )}

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <XCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-red-600" />
                <div>
                  <h3 className="font-medium text-red-800">Error validating contract names</h3>
                  <p className="mt-1 text-sm text-red-700">
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {(error as any)?.body?.detail || error.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && validationResults && (
          <Tabs defaultValue="validation" className="flex flex-col space-y-4">
            <TabsList className="self-start">
              <TabsTrigger value="validation">Validation Results</TabsTrigger>
              <TabsTrigger value="rawJson">Raw Validation JSON</TabsTrigger>
            </TabsList>

            <TabsContent value="validation" className="flex-1">
              <div className="flex flex-col gap-4">
                {validationResults.warnings.length === 0 ? (
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600" />
                        <p className="text-lg font-medium text-green-800">All contract names are valid!</p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-amber-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-amber-800">
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                        <span>
                          Found {validationResults.warnings.length} contract name{" "}
                          {validationResults.warnings.length === 1 ? "issue" : "issues"}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="rounded-md border">
                        <div className="bg-muted p-4 text-sm">
                          <p className="font-medium">Missing contract names</p>
                          <p className="mt-1 text-muted-foreground">
                            Please ensure that in the Excel we have the correct contract names for the following
                            records, or update them in the administratie.contracts table to validate them automatically
                            in the future
                          </p>
                        </div>
                        <div className="divide-y">
                          {validationResults.warnings.map((warning, index) => (
                            <div key={index} className="p-4 text-sm">
                              {warning}
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {validationResults.success.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span>Valid Contract Names</span>
                        <div className="ml-2 bg-green-50 text-green-700">{validationResults.success.length}</div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                        {validationResults.success.map((name, index) => (
                          <div
                            key={index}
                            className="rounded-md border border-green-100 bg-green-50 px-3 py-2 text-sm text-green-800"
                          >
                            {name}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="rawJson" className="flex-1">
              <div className="relative rounded-md border bg-muted p-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-2 top-2"
                  onClick={() => copyToClipboard(validationResults)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <pre className="max-h-[70vh] overflow-auto text-xs">{JSON.stringify(validationResults, null, 2)}</pre>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
